// Mock data for Evan Magee's portfolio
// Imagery sourced from his portfolio PDF (extracted as page images).

export const profile = {
  name: "Evan Magee",
  monogram: "EM",
  roles: [
    "Senior Creative Producer",
    "Associate Creative Director",
  ],
  tagline:
    "Producing, problem solving, and building visual languages for series, brands, and everything in between.",
  location: "Los Angeles, CA",
  timezone: "America/Los_Angeles",
  timezoneLabel: "LA",
  availability: "Selectively taking on new work",
  resume: "/files/Evan_Magee_Resume.pdf",
  email: "evanjamesmagee@gmail.com",
  phone: "401-203-1041",
  linkedin: "https://linkedin.com/in/evanmagee",
  linkedinLabel: "linkedin.com/in/evanmagee",
};

export const intro = {
  eyebrow: "Introduction / 01",
  headline:
    "A show\u2019s identity isn\u2019t just its logo \u2014 it\u2019s every frame a viewer sees before the story starts, and every graphic that keeps them oriented once it does.",
  paragraphs: [
    "For over a decade I\u2019ve built complete visual languages for series and brands \u2014 from main titles and episodic toolkits to network rebrands, pitch decks, and identity systems for startups.",
    "The work here spans Discovery, Netflix, HBO, National Geographic, YouTube Originals, and beyond \u2014 from the kinetic energy of Shark Week to the documentary weight of Immigration Nation.",
  ],
};

export const disciplines = [
  {
    id: "show-identity",
    number: "01",
    title: "Show Identity",
    subtitle:
      "Titles, on-air packages, episodic toolkits, lower thirds, bumpers, VFX.",
    description:
      "Building the complete visual language for a series \u2014 the main title, the episodic package, the tool-kits that carry that look through an entire season.",
  },
  {
    id: "brand-systems",
    number: "02",
    title: "Brand Systems",
    subtitle:
      "Network rebrands, visual identity, logo design, brand guidelines.",
    description:
      "Full network rebrands and identity systems that hold together across every touchpoint \u2014 from on-air to out-of-home to the phone in your pocket.",
  },
  {
    id: "design-direction",
    number: "03",
    title: "Design & Direction",
    subtitle: "Logo design, pitch decks, graphics, collateral.",
    description:
      "Graphic design and creative direction applied wherever it\u2019s needed \u2014 for clients across entertainment, sport, nonprofit, and consumer brands.",
  },
];

// helper to build /projects/page-XX.jpg paths
const p = (n) => `/projects/page-${String(n).padStart(2, "0")}.jpg`;

export const projects = [
  {
    id: "immigration-nation",
    index: "01",
    title: "Immigration Nation",
    client: "Netflix",
    category: "Show Identity",
    categoryId: "show-identity",
    year: "2020",
    role: "Main Title Sequence / In-Show GFX",
    award: "Peabody Award \u00b7 Film Independent Spirit Award",
    description:
      "Award-winning documentary series \u2014 #1 on Netflix. Developed the main title sequence and a comprehensive in-show graphics package covering maps, infographics, and document typography.",
    cover: p(4),
    gallery: [
      { src: p(4), caption: "Title treatment / award winning" },
      { src: p(5), caption: "Main title sequence stills" },
      { src: p(6), caption: "In-show graphics package \u2014 maps & infographics" },
    ],
    tags: ["Main Title", "GFX Package", "Infographics"],
    featured: true,
  },
  {
    id: "unnatural-selection",
    index: "02",
    title: "Unnatural Selection",
    client: "Netflix",
    category: "Show Identity",
    categoryId: "show-identity",
    year: "2019",
    role: "Main Title Sequence",
    description:
      "A main title sequence built around the language of the genome \u2014 DNA sequences, genetic code, and the ethical unknowns of gene editing rendered as kinetic type.",
    cover: p(7),
    gallery: [
      { src: p(7), caption: "Title concept treatment" },
      { src: p(8), caption: "Main title sequence frames" },
    ],
    tags: ["Main Title", "Kinetic Type"],
  },
  {
    id: "strive",
    index: "03",
    title: "Strive",
    client: "YouTube Originals",
    category: "Show Identity",
    categoryId: "show-identity",
    year: "2018",
    role: "In-Show Graphics",
    description:
      "In-show graphic system for a YouTube Originals sports documentary \u2014 scoring mechanics, maps and historical framing, all reading at a glance.",
    cover: p(9),
    gallery: [
      { src: p(9), caption: "Title treatment / key art" },
      { src: p(10), caption: "In-show graphic examples" },
    ],
    tags: ["In-Show GFX", "Sports"],
  },
  {
    id: "ssk",
    index: "04",
    title: "Surviving a Serial Killer",
    client: "People Magazine \u00b7 HBO",
    category: "Show Identity",
    categoryId: "show-identity",
    year: "2021",
    role: "Main Title / In-Show GFX",
    description:
      "A true-crime title sequence and graphics toolkit balancing atmosphere and respect \u2014 survivor portraits, archival materials, and location mapping.",
    cover: p(11),
    gallery: [
      { src: p(11), caption: "Title treatment" },
      { src: p(12), caption: "Main title sequence frames" },
      { src: p(13), caption: "In-show graphic examples" },
      { src: p(14), caption: "Additional People Magazine shows" },
    ],
    tags: ["True Crime", "Main Title"],
    featured: true,
  },
  {
    id: "shark-tank-shark-week",
    index: "05",
    title: "Shark Tank Meets Shark Week",
    client: "Discovery \u00b7 ABC",
    category: "Show Identity",
    categoryId: "show-identity",
    year: "2022",
    role: "Logo / Open",
    description:
      "A crossover logo and open that lives half above the city and half below the waterline \u2014 two pop-culture institutions colliding.",
    cover: p(15),
    gallery: [{ src: p(15), caption: "Logo / open key art" }],
    tags: ["Logo", "Open"],
  },
  {
    id: "travel-channel",
    index: "06",
    title: "Travel Channel",
    client: "Warner Bros. Discovery",
    category: "Brand Systems",
    categoryId: "brand-systems",
    year: "2018",
    role: "Global Rebrand",
    description:
      "Full global rebrand of Travel Channel \u2014 a new logomark and a system that scales from on-air packages to Times Square billboards and subway dominations.",
    cover: p(17),
    gallery: [
      { src: p(17), caption: "Trvl Channel logomark" },
      { src: p(18), caption: "Brand system asset examples" },
    ],
    tags: ["Rebrand", "Identity System", "OOH"],
    featured: true,
  },
  {
    id: "discovery-channel",
    index: "07",
    title: "Discovery Channel",
    client: "Warner Bros. Discovery",
    category: "Brand Systems",
    categoryId: "brand-systems",
    year: "2017",
    role: "Global Rebrand",
    description:
      "Global rebrand work for Discovery Channel \u2014 on-air promo packages, episodic toolkits and show graphics spanning Deadliest Catch, Naked and Afraid, and more.",
    cover: p(19),
    gallery: [
      { src: p(19), caption: "Discovery global rebrand" },
      { src: p(20), caption: "On-air asset examples" },
    ],
    tags: ["Rebrand", "On-Air"],
  },
  {
    id: "klg-pr",
    index: "08",
    title: "KLG PR",
    client: "Kate Laufer Gorenstien",
    category: "Brand Systems",
    categoryId: "brand-systems",
    year: "2023",
    role: "Rebrand / Collateral / Deck",
    description:
      "A full rebrand of a boutique PR agency \u2014 logo and collateral exploration, business system, and a capabilities deck covering influencer marketing strategy.",
    cover: p(21),
    gallery: [
      { src: p(21), caption: "Brand identity" },
      { src: p(22), caption: "Logo & collateral exploration" },
      { src: p(23), caption: "Capabilities deck" },
    ],
    externalLink: {
      url: "https://drive.google.com/file/d/1QEQAF0hoyqaBGzAuvWSAa4Qei4t4Wx--/view?usp=share_link",
      label: "View full capabilities deck",
    },
    tags: ["Identity", "Collateral", "Deck"],
  },
  {
    id: "roka",
    index: "09",
    title: "ROKA",
    client: "ROKA Sports",
    category: "Design & Direction",
    categoryId: "design-direction",
    year: "2016",
    role: "Product / Website / Graphic Design",
    description:
      "Performance-sport brand work for ROKA \u2014 product, website, and campaign imagery for swimmers, cyclists, and triathletes.",
    cover: p(25),
    gallery: [{ src: p(25), caption: "Product & campaign imagery" }],
    tags: ["Product", "Web", "Campaign"],
  },
  {
    id: "pitch-decks",
    index: "10",
    title: "Ideation & Pitch Decks",
    client: "Discovery \u00b7 Audible \u00b7 BuzzFeed \u00b7 Radley",
    category: "Design & Direction",
    categoryId: "design-direction",
    year: "2019\u201324",
    role: "Creative Direction / Design",
    description:
      "A curated collection of pitch and ideation decks built to help rooms believe in projects that don\u2019t exist yet \u2014 click any deck below to view it in full.",
    cover: p(26),
    gallery: [],
    decks: [
      {
        id: "mansplaining-history",
        title: "Mansplaining History",
        subtitle: "Series pitch",
        image: "/projects/decks/mansplaining-history.jpg",
        url: "https://drive.google.com/file/d/1-bS9uf5R1Bq1dRO_VaVkQ9jvKxxCUcXX/view?usp=share_link",
      },
      {
        id: "news-inc",
        title: "News Inc.",
        subtitle: "Series pitch",
        image: "/projects/decks/news-inc.jpg",
        url: "https://drive.google.com/file/d/1Vz36toikN0YsT1mFsjDqaV6apQ5SqOVB/view?usp=share_link",
      },
      {
        id: "impossible-town",
        title: "Impossible Town",
        subtitle: "Series pitch",
        image: "/projects/decks/impossible-town.jpg",
        url: "https://drive.google.com/file/d/1WUiDMgaS65i8O8S8S-JHKuyOkart7Ys4/view?usp=share_link",
      },
      {
        id: "on-halloween-night",
        title: "On Halloween Night",
        subtitle: "Two-part television event",
        image: "/projects/decks/on-halloween-night.jpg",
        url: "https://drive.google.com/file/d/1ZQ_reCs4BcrIsOGdlMyghwgtZSnAT6PE/view?usp=share_link",
      },
      {
        id: "sea-in-the-sea",
        title: "Sea in the Sea",
        subtitle: "Series pitch",
        image: "/projects/decks/sea-in-the-sea.jpg",
        url: "https://drive.google.com/file/d/1vKd-JLAhugfvqu4brG5JNq6Mm3Ejswih/view?usp=share_link",
      },
      {
        id: "under-siege",
        title: "Under Siege",
        subtitle: "The Death of Detective Sean Suiter \u00b7 Audible",
        image: "/projects/decks/under-siege.jpg",
        url: "https://drive.google.com/file/d/1d-F4-e1PRDb5-KPa7ta8XQgz3aJmnk5L/view?usp=share_link",
      },
      {
        id: "tiger-shark-buffet",
        title: "Tiger Shark Buffet",
        subtitle: "Presented to Discovery",
        image: "/projects/decks/tiger-shark-buffet.jpg",
        url: "https://drive.google.com/file/d/1U-kGW5Z-xxosXZU7mt1WyDRXiaur_Spx/view?usp=share_link",
      },
      {
        id: "i-killed-them-all",
        title: "I Killed Them All",
        subtitle: "The double life of El Mano Negra \u00b7 Radley",
        image: "/projects/decks/i-killed-them-all.jpg",
        url: "https://drive.google.com/file/d/1z1DRxcv3cWQ4juwgpo7sAYs-nSeaLTVY/view?usp=share_link",
      },
      {
        id: "loving",
        title: "Loving",
        subtitle: "Four-part limited series \u00b7 BuzzFeed Studios",
        image: "/projects/decks/loving.jpg",
        url: "https://drive.google.com/file/d/1fVB5ikRYSu_b0upT96bjeEY_YC2U0JbY/view?usp=share_link",
      },
    ],
    tags: ["Decks", "Ideation"],
  },
  {
    id: "house-of-suntory",
    index: "11",
    title: "House of Suntory",
    client: "Suntory Global Spirits",
    category: "Design & Direction",
    categoryId: "design-direction",
    year: "2024",
    role: "Global PR Guidelines Deck",
    description:
      "A global PR guidelines deck for the House of Suntory portfolio \u2014 Yamazaki, Hakushu, Hibiki, Toki, Haku, Roku \u2014 covering programs, tours, and gifting strategy.",
    cover: p(27),
    gallery: [
      { src: p(27), caption: "PR guidelines deck cover" },
      { src: p(28), caption: "Global PR guidelines content" },
    ],
    externalLink: {
      url: "https://drive.google.com/file/d/103pF1Q3I5YOkhVNyHk0YswlP0cKvdgaI/view?usp=share_link",
      label: "View full PR guidelines deck",
    },
    tags: ["Global PR", "Guidelines"],
    featured: true,
  },
];

export const clients = [
  "Netflix",
  "HBO",
  "Discovery",
  "National Geographic",
  "YouTube Originals",
  "Travel Channel",
  "People Magazine",
  "Audible",
  "BuzzFeed Studios",
  "Suntory",
  "ROKA",
  "Warner Bros.",
];

export const services = [
  "Creative Direction",
  "Show Identity & Main Titles",
  "Network Rebrands",
  "Brand Systems & Guidelines",
  "Logo Design",
  "Pitch & Ideation Decks",
  "Episodic Toolkits / Lower Thirds",
  "Collateral & Campaign Design",
];

export const aboutCopy = {
  eyebrow: "About / 02",
  heading:
    "Part producer, part art director — fluent in both areas.",
  body: [
    "I\u2019m a Senior Creative Producer and Associate Creative Director based in Los Angeles. I work with networks, streamers, and studios to build the identity of shows before, during, and after the story plays.",
    "Outside of series work, I lead design and creative direction for pitch decks, logos, brand systems, and consumer brand collateral \u2014 partnering with teams from first idea through final delivery.",
  ],
};
